/**
 * Public, login-free magic-link page where a new Velcor customer uploads
 * the 2 verification documents (ID + proof of address) requested by the
 * chat wizard when their fraud_score is elevated.
 *
 * Lifecycle:
 *  1. Page loads, reads `?t=<jwt>` from the URL and pings the public
 *     `/api/public/docs/{lead_id}` endpoint to validate the token and
 *     fetch the order summary.
 *  2. Customer picks 2 files (PDF / image), confirms, hits Submit.
 *  3. Files POST to `/api/public/docs/{lead_id}/upload`. On success we
 *     show a "thank you" state — no portal account, no second step.
 *
 * Tokens are HS256-signed with the same JWT_SECRET we use for portal
 * auth, scoped to `docs_upload` and tied to a specific lead_id. They
 * expire after 14 days.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useSearchParams, Link } from "react-router-dom";
import {
  Loader2, CheckCircle2, AlertTriangle, Upload, FileText, ShieldCheck,
  ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  es: {
    title: "Sube tus documentos de verificación",
    sub: "Necesitamos una verificación rápida para activar tu servicio.",
    order: "Orden",
    address: "Dirección de servicio",
    id_label: "Identificación oficial",
    id_hint: "Licencia de conducir, ID estatal o pasaporte (PDF o foto)",
    addr_label: "Comprobante de domicilio",
    addr_hint: "Factura de servicios reciente, contrato de arrendamiento o documento oficial con tu nombre + la dirección",
    pick_file: "Elegir archivo",
    drop_or: "o arrastra aquí",
    submit: "Enviar documentos",
    success_title: "¡Documentos recibidos!",
    success_body: "Nuestro equipo revisará tus archivos en menos de 24 horas y te avisaremos por email cuando aprobemos tu activación.",
    back_home: "Volver al inicio",
    error_invalid: "Este link ya no es válido o expiró. Solicita uno nuevo a soporte.",
    error_generic: "No pudimos completar el envío. Inténtalo de nuevo en un momento.",
    too_big: "El archivo supera los 10 MB. Toma una foto más comprimida o usa PDF.",
    bad_type: "Tipo de archivo no soportado. Usa JPG, PNG, HEIC o PDF.",
    privacy_line: "Tus archivos se cifran y solo el equipo de operaciones de Velcor tiene acceso.",
    already_uploaded: "Ya recibimos tus documentos · estamos revisándolos.",
  },
  en: {
    title: "Upload your verification documents",
    sub: "Quick check before we activate your service.",
    order: "Order",
    address: "Service address",
    id_label: "Government-issued ID",
    id_hint: "Driver's license, state ID, or passport (PDF or photo)",
    addr_label: "Proof of address",
    addr_hint: "Recent utility bill, lease, or official document with your name + the service address",
    pick_file: "Choose file",
    drop_or: "or drop here",
    submit: "Send documents",
    success_title: "Documents received!",
    success_body: "Our team will review your files in under 24 hours and email you when your activation is approved.",
    back_home: "Back to home",
    error_invalid: "This link is no longer valid or has expired. Contact support for a new one.",
    error_generic: "We couldn't complete the upload. Please try again in a moment.",
    too_big: "File exceeds 10 MB. Use a smaller photo or a PDF.",
    bad_type: "Unsupported file type. Use JPG, PNG, HEIC, or PDF.",
    privacy_line: "Your files are encrypted at rest — only the Velcor ops team has access.",
    already_uploaded: "We already received your documents · review in progress.",
  },
};

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/png", "image/heic", "image/heif",
                  "image/webp", "application/pdf"];

export default function UploadDocs() {
  const { leadId } = useParams();
  const [search] = useSearchParams();
  const token = search.get("t") || "";
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorFatal, setErrorFatal] = useState("");
  const [idFile, setIdFile] = useState(null);
  const [addrFile, setAddrFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/public/docs/${leadId}?t=${encodeURIComponent(token)}`
        );
        if (!cancel) {
          setInfo(data);
          if (data?.docs_uploaded) setSubmitted(true);
        }
      } catch (err) {
        if (!cancel) {
          const status = err?.response?.status;
          setErrorFatal(status === 401 ? "invalid" : "generic");
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [leadId, token]);

  const lang = (info?.language || "es").startsWith("en") ? "en" : "es";
  const t = COPY[lang];

  const validateFile = (f) => {
    if (!f) return null;
    if (!ALLOWED.includes(f.type)) return t.bad_type;
    if (f.size > MAX_BYTES) return t.too_big;
    return null;
  };

  const onPickId = (e) => {
    const f = e.target.files?.[0] || null;
    const err = validateFile(f);
    if (err) { setSubmitErr(err); e.target.value = ""; return; }
    setSubmitErr(""); setIdFile(f);
  };

  const onPickAddr = (e) => {
    const f = e.target.files?.[0] || null;
    const err = validateFile(f);
    if (err) { setSubmitErr(err); e.target.value = ""; return; }
    setSubmitErr(""); setAddrFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!idFile || !addrFile || submitting) return;
    setSubmitErr(""); setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("id_file", idFile);
      fd.append("address_file", addrFile);
      await axios.post(
        `${API}/public/docs/${leadId}/upload?t=${encodeURIComponent(token)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setSubmitted(true);
    } catch (err) {
      const det = err?.response?.data?.detail;
      setSubmitErr(typeof det === "string" ? det : t.error_generic);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="w-7 h-7 animate-spin text-velcor-green" />
      </div>
    );
  }

  if (errorFatal) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm" data-testid="docs-error-card">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
          <h1 className="text-velcor-ink text-xl font-extrabold mb-2">Link inválido</h1>
          <p className="text-slate-600 text-[13.5px] leading-relaxed mb-6">
            {errorFatal === "invalid" ? COPY.es.error_invalid : COPY.es.error_generic}
          </p>
          <Link to="/" className="inline-flex items-center gap-1.5 text-velcor-green font-bold text-[13px] underline-offset-2 hover:underline">
            <ArrowRight className="w-4 h-4" />Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm" data-testid="docs-success-card">
          <CheckCircle2 className="w-12 h-12 text-velcor-green mx-auto mb-4" />
          <h1 className="text-velcor-ink text-2xl font-extrabold mb-2">{t.success_title}</h1>
          <p className="text-slate-600 text-[14px] leading-relaxed mb-6">{t.success_body}</p>
          <p className="text-slate-500 text-[11.5px] mb-4">{info?.docs_uploaded ? t.already_uploaded : ""}</p>
          <Link to="/" className="inline-flex items-center gap-1.5 text-velcor-green font-bold text-[13px]">
            <ArrowRight className="w-4 h-4" />{t.back_home}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-xl mx-auto">
        {/* Logo / header */}
        <div className="text-center mb-8">
          <div className="inline-block text-velcor-ink font-extrabold tracking-[0.06em] text-[15px] mb-1">
            VELCOR · ENERGY
          </div>
          <div className="text-slate-400 text-[11px] uppercase tracking-[0.15em]">
            {t.order} #{info?.order_number || "—"}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="docs-form-card">
          <div className="px-6 py-5 border-b border-slate-100">
            <h1 className="text-velcor-ink text-xl font-extrabold mb-1">{t.title}</h1>
            <p className="text-slate-500 text-[13px] leading-relaxed">{t.sub}</p>
          </div>

          {/* Order summary */}
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/60">
            <div className="text-slate-400 text-[10.5px] uppercase tracking-[0.1em] mb-1">{t.address}</div>
            <div className="text-velcor-ink text-[13.5px] font-bold leading-tight">
              {info?.address || "—"}<br />
              <span className="text-slate-500 font-normal text-[12.5px]">
                {info?.city}, TX {info?.zip}
              </span>
            </div>
          </div>

          <form onSubmit={onSubmit} className="px-6 py-5 space-y-5">
            {/* ID file */}
            <FileSlot
              kind="id"
              label={t.id_label}
              hint={t.id_hint}
              pickText={t.pick_file}
              file={idFile}
              onPick={onPickId}
              icon={ShieldCheck}
            />

            {/* Address file */}
            <FileSlot
              kind="address"
              label={t.addr_label}
              hint={t.addr_hint}
              pickText={t.pick_file}
              file={addrFile}
              onPick={onPickAddr}
              icon={FileText}
            />

            {submitErr && (
              <div className="text-rose-600 text-[12.5px] font-bold bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="docs-submit-error">
                {submitErr}
              </div>
            )}

            <button
              type="submit"
              disabled={!idFile || !addrFile || submitting}
              className="w-full h-12 rounded-lg bg-velcor-ink text-white font-extrabold text-[14px] hover:bg-velcor-navy disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="docs-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {t.submit}
            </button>

            <p className="text-slate-400 text-[10.5px] leading-relaxed text-center">
              {t.privacy_line}
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

function FileSlot({ kind, label, hint, pickText, file, onPick, icon: Icon }) {
  return (
    <label className="block cursor-pointer" data-testid={`docs-slot-${kind}`}>
      <div className="text-velcor-ink font-bold text-[12.5px] mb-1.5 flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-velcor-green" />{label}
      </div>
      <div className={`border-2 border-dashed rounded-lg px-4 py-4 transition ${
        file ? "border-velcor-green bg-velcor-green/5" : "border-slate-300 bg-slate-50/30 hover:border-velcor-green hover:bg-velcor-green/[0.03]"
      }`}>
        {file ? (
          <div className="flex items-center gap-2 text-velcor-ink text-[13px] font-bold">
            <CheckCircle2 className="w-4 h-4 text-velcor-green flex-shrink-0" />
            <span className="truncate">{file.name}</span>
            <span className="ml-auto text-slate-500 text-[11px] font-normal">
              {(file.size / 1024 / 1024).toFixed(1)} MB
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Upload className="w-5 h-5 text-slate-400" />
            <div className="text-slate-600 text-[12.5px]">
              <span className="font-bold text-velcor-ink">{pickText}</span>
              <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>
            </div>
          </div>
        )}
      </div>
      <input
        type="file"
        accept=".jpg,.jpeg,.png,.heic,.heif,.webp,.pdf,image/*,application/pdf"
        onChange={onPick}
        className="hidden"
        data-testid={`docs-input-${kind}`}
      />
    </label>
  );
}
